import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { Tile, TileStripe } from "@dash/tile";
import { useToast } from "@dash/toast";
import { css, useTheme } from "@emotion/react";
import Decimal from "decimal.js";
import { useEffect, useState } from "react";
import { MdCheckCircle, MdDelete, MdLocalShipping } from "react-icons/md";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom-v5-compat";

import { Order, Package, PackageItem } from "@megaron/fulfillment-contracts";
import { useClientManager } from "@megaron/react-clients";
import { PackageSizeDialog, packageSizeLabelMap } from "./PackageSizeDialog";

import { packageWeightLimitKg } from "./constants";
import { isOrderPackingLocked } from "./orderPackingUtils";

export const statusTitleMap: Record<string, string> = {
  created: "Utworzę zlecenie odbioru",
  confirmed: "Potwierdzona przez nadawcę",
  stored_by_sender: "Przygotowana przez nadawcę",
  dispatched_by_sender: "Wydana przez nadawcę",
  collected_from_sender: "Odebrana od nadawcy",
  adopted_at_source_branch: "Przyjęta w oddziale nadawczym",
  sent_from_source_branch: "Wysłana z oddziału nadawczego",
  adopted_at_sorting_center: "Przyjęta w sortowni",
  sent_from_sorting_center: "Wysłana z sortowni",
  adopted_at_target_branch: "Przyjęta w oddziale doręczającym",
  out_for_delivery: "W doręczeniu",
  ready_to_pickup: "Gotowa do odbioru",
  pickup_reminder_sent: "Wysłano przypomnienie o odbiorze",
  pickup_time_expired: "Minął termin odbioru",
  undelivered: "Niedoręczona",
  returned_to_sender: "Zwrócona do nadawcy",
  delivered: "Doręczona",
  canceled: "Anulowana",
  out_for_delivery_to_address: "W doręczeniu pod adres",
};

type Props = {
  order: Order;
  packages: Package[];
  activeId: number | null;
  orderItems: PackageItem[];
  getTotalOrderQuantity: (variantId: string) => number;
  queryKey: string | string[];
  onSetActiveId: (id: number | null) => void;
  onQuantityChange: (packages: Package[]) => void;
  getDisplayName: (variantId: string, fallbackName: string) => string;
};

const printPdfInline = (base64: string) => {
  const bin = atob(base64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const cleanup = () => {
    URL.revokeObjectURL(url);
    if (iframe.isConnected) document.body.removeChild(iframe);
    window.removeEventListener("afterprint", cleanup);
  };
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        const a = document.createElement("a");
        a.href = url;
        a.download = "label.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }, 200);
  };
  window.addEventListener("afterprint", cleanup);
  iframe.src = url;
  setTimeout(cleanup, 60000);
};

export const PackageList = ({
  order,
  packages,
  activeId,
  orderItems,
  getTotalOrderQuantity,
  onSetActiveId,
  onQuantityChange,
  queryKey,
  getDisplayName,
}: Props) => {
  const theme = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { orderId } = useParams<{ orderId: string }>();
  const [dialogIndex, setDialogIndex] = useState<number | null>(null);
  const [showOverweightDialog, setShowOverweightDialog] = useState(false);
  const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
  const [selectSizeDialogIndex, setSelectSizeDialogIndex] = useState<number | null>(null);
  const [pendingGenerateLabelIndex, setPendingGenerateLabelIndex] = useState<number | null>(null);
  const labelMutation = useClientManager("fulfillment").generateLabelForPackage().useMutation();
  const savePackageMutation = useClientManager("fulfillment").saveOrderPackage().useMutation();
  const isOrderLocked = isOrderPackingLocked(order);

  const getPackageWeight = (pkg: Package): number => {
    let total = new Decimal(0);
    for (const item of pkg.items) {
      const full = orderItems.find((i) => i.variantId === item.variantId);
      if (!full) continue;
      total = total.plus(new Decimal(full.weight).mul(item.quantity));
    }
    return Number(total.toFixed(1));
  };

  const getPackedCount = (variantId: string): number => {
    return packages.reduce((acc, pkg) => {
      const item = pkg.items.find((i) => i.variantId === variantId);
      return acc + (item?.quantity ?? 0);
    }, 0);
  };

  const handleQuantityChange = (pkgIdx: number, variantId: string, delta: number) => {
    const full = orderItems.find((i) => i.variantId === variantId);
    if (!full) return;
    const totalOrderQty = getTotalOrderQuantity(variantId);
    const pkg = packages[pkgIdx];
    if (delta > 0) {
      const alreadyPacked = getPackedCount(variantId);
      if (alreadyPacked >= totalOrderQty) {
        setShowUnavailableDialog(true);
        return;
      }
      const currentWeight = getPackageWeight(pkg);
      const projectedWeight = new Decimal(currentWeight).plus(full.weight).toNumber();
      if (projectedWeight > packageWeightLimitKg) {
        setShowOverweightDialog(true);
        return;
      }
    }
    const updated = packages.map((p, i) =>
      i !== pkgIdx
        ? p
        : {
            ...p,
            items: p.items
              .map((item) => (item.variantId !== variantId ? item : { ...item, quantity: item.quantity + delta }))
              .filter((item) => item.quantity > 0),
          },
    );
    onQuantityChange(updated);
  };

  const handleDelete = (index: number) => {
    const updated = packages.filter((_, i) => i !== index);
    onQuantityChange(updated);
    if (activeId === index + 1) {
      onSetActiveId(null);
    }
    setDialogIndex(null);
  };

  const handleGenerateLabel = async (index: number) => {
    if (!orderId) return;
    const pkg = packages[index];
    if (!pkg.trackingNumber && !pkg.size) {
      setSelectSizeDialogIndex(index);
      return;
    }
    labelMutation.mutate(
      { packageId: pkg.id },
      {
        onSuccess: (res) => {
          if (res.labelBase64) {
            printPdfInline(res.labelBase64);
          }
          queryClient.invalidateQueries(queryKey);
        },
        onError: () => {
          toast.error("Błąd generowania etykiety");
        },
      },
    );
  };

  useEffect(() => {
    if (pendingGenerateLabelIndex !== null) {
      const pkg = packages[pendingGenerateLabelIndex];
      if (pkg.size) {
        handleGenerateLabel(pendingGenerateLabelIndex);
        setPendingGenerateLabelIndex(null);
      }
    }
  }, [packages]);

  return (
    <>
      {packages.map((pkg, pkgIdx) => {
        const isActive = activeId === pkgIdx + 1;
        const isActiveAndEditable = isActive && !isOrderLocked && !pkg.isLabelScanned;
        const weight = getPackageWeight(pkg).toFixed(1);
        const statusDisplay = pkg.trackingStatus ? statusTitleMap[pkg.trackingStatus] ?? pkg.trackingStatus : null;

        return (
          <Tile
            key={pkgIdx}
            onClick={() => {
              if (!isOrderLocked) onSetActiveId(pkgIdx + 1);
            }}
            stripe={<TileStripe color={isActive ? theme.colors.primary : theme.colors.primaryLight} />}
            gap="12px"
            css={{ cursor: isOrderLocked ? "default" : "pointer", position: "relative" }}
          >
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "nowrap",
                width: "100%",
                gap: "1rem",
                position: "relative",
              }}
            >
              <div css={{ display: "flex", flexDirection: "column", flexShrink: 1, flexGrow: 1 }}>
                <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  <span css={{ fontSize: "16px", color: theme.colors.primary, fontWeight: 600 }}>{pkg.name}</span>
                  <span css={{ fontSize: "13px", color: theme.colors.default[600] }}>
                    {pkg.trackingNumber ?? "Brak numeru przesyłki"}
                  </span>
                  {pkg.dispatchOrderId && (
                    <div css={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "12px", color: theme.colors.success }}>
                      <MdCheckCircle size={16} />
                      <span>Odbior zlecony</span>
                    </div>
                  )}
                </div>
                {statusDisplay && (
                  <div css={{ marginTop: "2px", fontSize: "12px", color: theme.colors.default[700] }}>
                    {statusDisplay}
                  </div>
                )}
              </div>

              {pkg.size && (
                <div css={{ position: "absolute", top: 0, right: 0, fontSize: "12px", color: theme.colors.primary }}>
                  {packageSizeLabelMap[pkg.size]}
                </div>
              )}
            </div>

            {pkg.items.map((item) => (
              <div
                key={`${item.variantId}-${item.lineItemId ?? ""}`}
                css={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  fontSize: "0.95rem",
                  marginTop: "0.25rem",
                })}
              >
                <div css={{ display: "flex", alignItems: "center" }}>
                  <span>{getDisplayName(item.variantId, item.name)}</span>
                  <div
                    css={css({
                      flexGrow: 1,
                      borderBottom: "1px dashed #999",
                      margin: "0 0.5rem",
                      height: 1,
                    })}
                  />
                  {isActiveAndEditable ? (
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(pkgIdx, item.variantId, -1);
                      }}
                      css={{
                        border: "none",
                        background: "none",
                        fontSize: "1.1rem",
                        cursor: "pointer",
                      }}
                    >
                      &minus;
                    </button>
                    <span>{item.quantity} szt.</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(pkgIdx, item.variantId, 1);
                      }}
                      css={{
                        border: "none",
                        background: "none",
                        fontSize: "1.1rem",
                        cursor: "pointer",
                      }}
                    >
                      &#43;
                    </button>
                  </div>
                ) : (
                  <span>{item.quantity} szt.</span>
                )}
                </div>
                {item.engraving && (
                  <div css={{ fontSize: "0.85rem", color: theme.colors.default[600], marginLeft: 0 }}>
                    Grawerunek: &quot;{item.engraving}&quot;
                  </div>
                )}
              </div>
            ))}

            <div
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "0.5rem",
              }}
            >
              <div css={{ fontSize: "0.85rem" }}>{weight} / 25 kg</div>

              {isActiveAndEditable && (
                <div css={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    variant="outline"
                    icon={<MdDelete />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDialogIndex(pkgIdx);
                    }}
                  />
                  <Button
                    variant="outline"
                    icon={<MdLocalShipping />}
                    isLoading={labelMutation.isLoading || savePackageMutation.isLoading}
                    isDisabled={pkg.items.length === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateLabel(pkgIdx);
                    }}
                  >
                    {pkg.trackingNumber ? "Drukuj etykietę" : "Generuj etykietę"}
                  </Button>
                </div>
              )}
            </div>
          </Tile>
        );
      })}

      {dialogIndex !== null && (
        <Dialog header="Usuń paczkę" onClose={() => setDialogIndex(null)} css={{ maxWidth: "400px" }}>
          <div css={{ margin: "1rem 0" }}>
            Czy na pewno chcesz usunąć paczkę <strong>{packages[dialogIndex].name}</strong>?
          </div>
          <div css={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => setDialogIndex(null)}>
              Anuluj
            </Button>
            <Button onClick={() => handleDelete(dialogIndex)}>Usuń</Button>
          </div>
        </Dialog>
      )}

      {showOverweightDialog && (
        <Dialog
          header="Przekroczony limit wagi"
          onClose={() => setShowOverweightDialog(false)}
          css={{ maxWidth: "400px" }}
        >
          <div css={{ padding: "1rem 0" }}>
            Nie możesz dodać więcej produktów do tej paczki, ponieważ przekroczyłbyś maksymalny limit wagi 25 kg.
          </div>
          <div css={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setShowOverweightDialog(false)}>OK</Button>
          </div>
        </Dialog>
      )}

      {showUnavailableDialog && (
        <Dialog
          header="Brak dostępnych sztuk"
          onClose={() => setShowUnavailableDialog(false)}
          css={{ maxWidth: "400px" }}
        >
          <div css={{ padding: "1rem 0" }}>
            Nie możesz dodać więcej sztuk tego produktu – wszystkie zostały już zapakowane.
          </div>
          <div css={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setShowUnavailableDialog(false)}>OK</Button>
          </div>
        </Dialog>
      )}

      {selectSizeDialogIndex !== null && (
        <PackageSizeDialog
          onClose={() => {
            setSelectSizeDialogIndex(null);
          }}
          onSelect={(size) => {
            if (selectSizeDialogIndex === null || !orderId) return;
            const idx = selectSizeDialogIndex;
            const updated = packages.map((pkg, i) => (i !== idx ? pkg : { ...pkg, size }));
            onQuantityChange(updated);
            const changed = updated[idx];
            setSelectSizeDialogIndex(null);
            savePackageMutation.mutate(
              { orderId, package: changed },
              {
                onSuccess: () => {
                  setPendingGenerateLabelIndex(idx);
                  queryClient.invalidateQueries(queryKey);
                },
                onError: () => {
                  toast.error("Nie udało się zapisać gabarytu paczki");
                },
              },
            );
          }}
        />
      )}
    </>
  );
};
